/**
 * 認證相關 API
 */

import axios from './axios';
import { LoginRequest, TokenResponse, UserProfile } from '../types';

export const authService = {
  /**
   * 取得 CAPTCHA 驗證碼
   */
  getCaptcha: async (): Promise<{ captcha_key: string; captcha_svg: string }> => {
    const response = await axios.get('/api/auth/captcha');
    return response.data;
  },

  /**
   * 使用者登入（舊 monolithic API · 已棄用 · 保留 3 個月 grace）
   * @deprecated 改用 factor chain：getPolicy / loginStart / loginFactor*
   */
  login: async (data: LoginRequest): Promise<TokenResponse> => {
    const response = await axios.post('/api/auth/login', data);
    return response.data;
  },

  // ── Login factor chain（Task-tenant-login-overhaul PR G）─────────────────────
  /** 查租戶登入政策 → {policy_code, factors} */
  getPolicy: async (username: string): Promise<{ policy_code: string; factors: string[] }> => {
    const response = await axios.post('/api/auth/policy', { username });
    return response.data;
  },

  /** 依 host 查租戶登入政策 + 啟用管道（Phase C1：決定是否顯「以 LINE 登入」）。 */
  getLoginConfig: async (): Promise<{ policy_code: string; factors: string[]; tenant_channels: string[] }> => {
    const response = await axios.post('/api/auth/policy', {});
    return { tenant_channels: [], ...response.data };
  },

  /** 建 pending session → {session_id, next_factor, prepare} */
  loginStart: async (payload: { username: string; return_url?: string | null; trigger?: string | null }) => {
    const response = await axios.post('/api/auth/login/start', payload);
    return response.data;
  },

  /** 一次帳密登入（porsche UX）：帳密同送，L01 回 token · L02/L03 回 next_factor / need_enrollment */
  loginOneshot: async (payload: { username: string; password: string; return_url?: string | null; trigger?: string | null }) => {
    const response = await axios.post('/api/auth/login/oneshot', payload);
    return response.data;
  },

  /** credential factor（username 由 pending session 注入，body 只帶密碼）· 保留 backward-compat */
  loginFactorCredential: async (payload: { session_id: string; password: string }) => {
    const response = await axios.post('/api/auth/login/factor/credential', payload);
    return response.data;
  },

  /** captcha factor */
  loginFactorCaptcha: async (payload: { session_id: string; captcha_key: string; captcha_code: string }) => {
    const response = await axios.post('/api/auth/login/factor/captcha', payload);
    return response.data;
  },

  /** totp factor（6 位數 TOTP 或 8 字元 backup code 皆送這裡） */
  loginFactorTotp: async (payload: { session_id: string; code: string }) => {
    const response = await axios.post('/api/auth/login/factor/totp', payload);
    return response.data;
  },

  /** 首次登入強制 enrollment · verify + 發 token + 10 組 backup（PR G-backend） */
  loginFactorTotpEnroll: async (payload: { session_id: string; code: string }) => {
    const response = await axios.post('/api/auth/login/factor/totp/enroll', payload);
    return response.data;
  },

  /** refresh 驗證碼（對已建 pending session） → {captcha_key, captcha_svg} */
  refreshLoginCaptcha: async (sessionId: string): Promise<{ captcha_key: string; captcha_svg: string }> => {
    const response = await axios.get(`/api/auth/captcha/${sessionId}`);
    return response.data;
  },

  /**
   * Admin 重置指定 user 的 2FA（刪 TOTP secret + backup + pending）
   * 後端 tenant 隔離：系統管理員跨租戶、租戶管理者限同租戶。走 user_logs Update（audit）。
   */
  adminReset2fa: async (userId: number): Promise<{ message: string }> => {
    const response = await axios.post('/api/auth/2fa/admin/reset', { user_id: userId });
    return response.data;
  },

  /**
   * 驗證 Session 有效性
   */
  verifySession: async (): Promise<{ valid: boolean; session_id?: string; user_id?: number; username?: string; account?: string }> => {
    try {
      const response = await axios.get('/api/auth/verify-session');
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        return { valid: false };
      }
      throw error;
    }
  },

  /**
   * 取得當前使用者資訊
   */
  getCurrentUser: async (): Promise<UserProfile> => {
    const response = await axios.get('/api/auth/me');
    return response.data;
  },

  /**
   * 使用者登出
   */
  logout: async (): Promise<void> => {
    await axios.post('/api/auth/logout');
    localStorage.removeItem('access_token');
  },

  /**
   * 檢查是否已登入
   */
  isAuthenticated: (): boolean => {
    return !!localStorage.getItem('access_token');
  },

  /**
   * 儲存 Token
   */
  saveToken: (token: string): void => {
    localStorage.setItem('access_token', token);
  },

  /**
   * 清除 Token
   */
  clearToken: (): void => {
    localStorage.removeItem('access_token');
  },
};
